import { useState } from "react";
import { Button } from "@/components/ui/Button";
import type { KratosFlow } from "@/lib/kratos";
import {
  firstFlowError,
  KratosSessionRefreshError,
  kratosErrorCopy,
  nodeMessages,
  submitFlow,
} from "@/lib/kratos";

/**
 * Set a new password via a Kratos settings flow. Used by recovery
 * (Kratos hands a privileged settings flow after a valid code) and by
 * the account security tab. Settings flows demand a recently
 * authenticated Kratos session — `onSessionRefreshRequired` lets the
 * caller prompt an inline re-auth instead of dead-ending.
 */
export function KratosPasswordForm({
  initialFlow,
  submitLabel = "Set new password",
  onSaved,
  onSessionRefreshRequired,
  testIdPrefix = "kratos-password",
}: {
  initialFlow: KratosFlow;
  submitLabel?: string;
  onSaved: () => void | Promise<void>;
  onSessionRefreshRequired?: () => void;
  testIdPrefix?: string;
}) {
  const [flow, setFlow] = useState(initialFlow);
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      const outcome = await submitFlow("settings", flow, {
        method: "password",
        password,
      });
      if (outcome.kind === "flow") {
        setFlow(outcome.flow);
        if (outcome.flow.state === "success") {
          setPassword("");
          await onSaved();
          return;
        }
      } else {
        setPassword("");
        await onSaved();
      }
    } catch (err) {
      if (err instanceof KratosSessionRefreshError && onSessionRefreshRequired) {
        onSessionRefreshRequired();
        return;
      }
      setError(kratosErrorCopy(err));
    } finally {
      setSubmitting(false);
    }
  };

  const flowError = firstFlowError(flow);
  const passwordMessages = nodeMessages(flow, "password");

  return (
    <form
      data-testid={`${testIdPrefix}-form`}
      className="flex flex-col gap-3"
      onSubmit={handleSubmit}
    >
      <label className="flex flex-col gap-1 text-xs">
        <span className="font-display uppercase tracking-wider text-ink-muted">
          New password
        </span>
        <input
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          autoComplete="new-password"
          required
          minLength={8}
          data-testid={`${testIdPrefix}-input`}
          className="rounded border border-rule bg-canvas px-3 py-2.5 text-sm text-ink focus:outline-none focus:ring-2 focus:ring-accent"
        />
        {passwordMessages.map((message) => (
          <span key={message} className="font-mono text-[0.65rem] text-destructive">
            {message}
          </span>
        ))}
      </label>
      {flowError ? (
        <p data-testid={`${testIdPrefix}-error`} className="font-mono text-xs text-destructive">
          {flowError}
        </p>
      ) : null}
      {error ? <p className="font-mono text-xs text-destructive">{error}</p> : null}
      <Button
        type="submit"
        disabled={submitting}
        data-testid={`${testIdPrefix}-submit`}
        className="self-end"
      >
        {submitting ? "Saving…" : submitLabel}
      </Button>
    </form>
  );
}
